import React, { createContext, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { notification } from "antd";
import { Creators as StockMovementActions } from "../../store/ducks/stockMovement";
import { findAll as findAllUnits } from "../../services/unitService";
import { getAllSuppliers } from "../../services/supplierService";
import { callStockItemIndex } from "../../services/stockItemService";
import {
  callParseNfeXml,
  callParseNfPdf,
  callStockMovementSave,
} from "../../services/stockMovementService";
import { buildLinesFromNfItems } from "../../utils/stockMovement";
import { callStoreInvoice } from "../../services/invoiceService";

export const StockMovementContext = createContext({});

export const StockMovementContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  const { groupSelected, farmSelected } = useSelector((state) => state.app);
  const stockMovementState = useSelector((state) => state.stockMovement);

  // Filtro local da tabela: ALL/ENTRY/EXIT/INACTIVE (ALL e INACTIVE são opções
  // locais; ENTRY/EXIT filtram por `type`).
  const [filterType, setFilterType] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [textSearchSupplier, setTextSearchSupplier] = useState("");

  // Lookups
  const [stockItems, setStockItems] = useState([]);
  const [isLoadingStockItems, setIsLoadingStockItems] = useState(false);
  const [variants, setVariants] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [measurementUnits, setMeasurementUnits] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  // Upload de NF
  const [xmlLoading, setXmlLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Alerta de NF duplicada
  const [duplicateNfAlert, setDuplicateNfAlert] = useState({
    show: false,
    message: "",
    missingItems: [],
  });

  const groupId = groupSelected?.id;
  const farmId = farmSelected?.id;

  useEffect(() => {
    if (groupId && farmId) {
      dispatch(StockMovementActions.indexStockMovement(groupId, farmId));
    }
  }, [groupId, farmId, dispatch]);

  const loadStockItems = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingStockItems(true);
    try {
      const {
        data: { results },
      } = await callStockItemIndex({ groupId, farmId });
      setStockItems(results || []);
    } catch {
      setStockItems([]);
    } finally {
      setIsLoadingStockItems(false);
    }
  }, [groupId, farmId]);

  // Variantes/lotes do item selecionado. No POC, resolvemos a partir do próprio
  // item carregado (item.variants). Quando o backend expuser o endpoint,
  // trocar por callStockItemVariants({ groupId, farmId, id: itemId }).
  const loadVariants = useCallback(
    (itemId) => {
      setIsLoadingVariants(true);
      try {
        const item = stockItems.find((i) => i.id === itemId);
        setVariants(item && Array.isArray(item.variants) ? item.variants : []);
      } finally {
        setIsLoadingVariants(false);
      }
    },
    [stockItems],
  );

  const loadBatches = useCallback(
    (itemId) => {
      setIsLoadingBatches(true);
      try {
        const item = stockItems.find((i) => i.id === itemId);
        setBatches(item && Array.isArray(item.batches) ? item.batches : []);
      } finally {
        setIsLoadingBatches(false);
      }
    },
    [stockItems],
  );

  const loadUnits = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingUnits(true);
    try {
      const {
        data: { results },
      } = await findAllUnits({ groupId, farmId });
      setMeasurementUnits(results || []);
    } catch {
      setMeasurementUnits([]);
    } finally {
      setIsLoadingUnits(false);
    }
  }, [groupId, farmId]);

  const loadSuppliers = useCallback(async () => {
    if (!groupId || !farmId) return;
    setIsLoadingSuppliers(true);
    try {
      const {
        data: { results },
      } = await getAllSuppliers({ groupId, farmId, withoutPagination: true });
      setSuppliers(results || []);
    } catch {
      setSuppliers([]);
    } finally {
      setIsLoadingSuppliers(false);
    }
  }, [groupId, farmId]);

  const handleSearch = (term) => setSearchTerm(term);

  const handleFilterChange = (type) => setFilterType(type);

  const handleOpenNewMovement = (type) => {
    loadStockItems();
    loadUnits();
    setTextSearchSupplier("");
    setDuplicateNfAlert({ show: false, message: "", missingItems: [] });
    dispatch(StockMovementActions.showModalNewStockMovement(type));
  };

  const handleRefresh = () => {
    dispatch(StockMovementActions.indexStockMovement(groupId, farmId));
  };

  const handleSaveMovement = async (
    lines,
    header,
    resetForm,
    resetLocalState,
  ) => {
    setIsLoadingRequest(true);
    try {
      let invoiceId = null;
      if (header?.documentFile && header?.documentParsed) {
        const { data } = await callStoreInvoice({
          groupId,
          farmId,
          file: header?.documentFile,
          parsedData: header?.documentParsed,
        });
        invoiceId = data?.id;
      }
      const payload = {
        ...header,
        invoiceId,
        itens: lines,
      };
      await callStockMovementSave({ groupId, farmId, movement: payload });
      setIsLoadingRequest(false);
      notification.success({
        message: "Movimento de estoque salvo com sucesso",
      });
      resetForm();
      resetLocalState();
      dispatch(StockMovementActions.hideModalNewStockMovement());
      dispatch(StockMovementActions.indexStockMovement(groupId, farmId));
    } catch (error) {
      setIsLoadingRequest(false);
      if (Object.keys(error).includes("response")) {
        const { response } = error;
        if (Object.keys(response).includes("data")) {
          const {
            data: { code: errorCode },
          } = response;
          if (errorCode === 5045) {
            notification.error({
              message:
                "Erro ao salvar movimento. N° de Documento NF-e duplicado.",
            });
          } else {
            notification.error({ message: "Erro ao salvar movimento." });
          }
        }
      } else {
        notification.error({ message: "Erro ao salvar movimento." });
      }
    }
  };

  const handleOpenDetail = (movement) => {
    dispatch(
      StockMovementActions.showStockMovement(groupId, farmId, movement.id),
    );
    dispatch(StockMovementActions.showDrawerStockMovement());
  };

  const handleCloseModal = () => {
    dispatch(StockMovementActions.hideModalNewStockMovement());
  };

  const handleCloseDrawer = () => {
    dispatch(StockMovementActions.hideDrawerStockMovement());
  };

  const handleRequestDelete = (id, requestUser) => {
    dispatch(
      StockMovementActions.requestDeleteStockMovement(
        groupId,
        farmId,
        id,
        requestUser,
      ),
    );
  };

  const addVariant = useCallback(
    (itemId, variant) => {
      const item = stockItems.find((i) => i.id === itemId);
      if (!item) return;
      const updatedVariants = [...(item.variants || []), variant];
      const updatedItem = { ...item, variants: updatedVariants };
      setStockItems((prevItems) =>
        prevItems.map((i) => (i.id === itemId ? updatedItem : i)),
      );
      setVariants(updatedVariants);
    },
    [stockItems],
  );

  const addBatch = useCallback(
    (itemId, batch) => {
      const item = stockItems.find((i) => i.id === itemId);
      if (!item) return;
      const updatedBatches = [...(item.batches || []), batch];
      const updatedItem = { ...item, batches: updatedBatches };
      setStockItems((prevItems) =>
        prevItems.map((i) => (i.id === itemId ? updatedItem : i)),
      );
      setBatches(updatedBatches);
    },
    [stockItems],
  );

  const closeDuplicateNfAlert = () =>
    setDuplicateNfAlert({ show: false, message: "", missingItems: [] });

  // Upload/parse de NF. Em sucesso, retornaria as linhas montadas
  // via buildLinesFromNfItems para popular o formulário.
  const handleUploadXml = useCallback(
    async (file) => {
      setXmlLoading(true);
      try {
        const { data } = await callParseNfeXml({ groupId, farmId, file });
        return buildLinesFromNfItems(data?.itens || []);
      } catch (error) {
        notification.info({
          message: "Importação de NF (XML) ainda não disponível.",
          description: error.message,
        });
        return null;
      } finally {
        setXmlLoading(false);
      }
    },
    [groupId, farmId],
  );

  const handleUploadPdf = useCallback(
    async (file) => {
      setPdfLoading(true);
      try {
        const { data } = await callParseNfPdf({ groupId, farmId, file });
        return buildLinesFromNfItems(data?.itens || []);
      } catch (error) {
        notification.info({
          message: "Importação de NF (PDF) ainda não disponível.",
          description: error.message,
        });
        return null;
      } finally {
        setPdfLoading(false);
      }
    },
    [groupId, farmId],
  );

  return (
    <StockMovementContext.Provider
      value={{
        groupId,
        farmId,
        filterType,
        searchTerm,
        stockItems,
        isLoadingStockItems,
        variants,
        batches,
        isLoadingVariants,
        isLoadingBatches,
        measurementUnits,
        isLoadingUnits,
        suppliers,
        isLoadingSuppliers,
        xmlLoading,
        pdfLoading,
        duplicateNfAlert,
        ...stockMovementState,
        handleSearch,
        handleFilterChange,
        handleOpenNewMovement,
        handleSaveMovement,
        handleOpenDetail,
        handleCloseModal,
        handleCloseDrawer,
        handleRequestDelete,
        closeDuplicateNfAlert,
        handleUploadXml,
        handleUploadPdf,
        handleRefresh,
        loadStockItems,
        loadVariants,
        loadUnits,
        loadSuppliers,
        loadBatches,
        addVariant,
        addBatch,
        isLoadingRequest,
        textSearchSupplier,
        setTextSearchSupplier,
      }}
    >
      {children}
    </StockMovementContext.Provider>
  );
};
